import MoreAboutItem from '@components/MoreAboutItem'
import SvgIcon from '@components/SvgIcon'
import useTracking, { TrackingFnType } from '@hooks/useTracking'
import { stripHtml } from '@hooks/useTracking/utils'
import useViewportTracking from '@hooks/useViewportTracking'
import useViewportType from '@hooks/useViewportType'
import { mobileCSS } from '@measures/responsive'
import { CookWidget } from '@widgets/types'
import {
  FunctionComponent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'
import styled, { css } from 'styled-components'
import { isValidMoreAboutProps, scrollToItem } from './utils'

type MoreAboutProps = Record<string, unknown>

const StyledMoreAbout = styled.div`
  ${({
    theme: {
      colors: {
        grey: { grey_10 },
      },
      measures: { stretch },
    },
  }) => css`
    box-sizing: border-box;
    padding: 24px 9px;
    background-color: ${grey_10};
    display: grid;
    grid-gap: 24px;
    ${stretch('left')}
    ${stretch('right')}
  `}
`

const StyledTitle = styled.div`
  ${({
    theme: {
      fonts: { goodOTNarrow: goodOTNarrowFontName },
    },
  }) => css`
    font-family: ${goodOTNarrowFontName};
    font-weight: 900;
    line-height: 1.1;
    font-size: 18px;
    margin-left: 15px;
  `}
`

const StyledItemsContainer = styled.div`
  display: grid;
  grid-auto-flow: column;
  grid-gap: 12px;
  width: 100%;
  overflow-x: auto;
  scroll-snap-type: x mandatory;
  scroll-behavior: smooth;
  -webkit-overflow-scrolling: touch;
  -ms-overflow-style: none; /* IE and Edge */
  scrollbar-width: none; /* Firefox */
  &::-webkit-scrollbar {
    display: none;
  }
`

interface StyledScrollingButtonProps {
  direction: 'prev' | 'next'
  isScrolling: boolean
  isVisible: boolean
}

const StyledScrollingButton = styled.button<StyledScrollingButtonProps>`
  ${({ isScrolling, direction, isVisible }) => css`
    ${direction === 'prev'
      ? css`
          left: 0;
        `
      : css`
          right: 0;
        `}
    ${!isScrolling || !isVisible
      ? css`
          display: none;
        `
      : css`
          appearance: none;
          -webkit-appearance: none;
          -moz-appearance: none;
          border: 0;
          padding: 0;
          background-color: transparent;
          position: absolute;
          top: 0;
          cursor: pointer;
          height: 100%;
          display: grid;
          align-items: center;
        `}

    ${mobileCSS(css`
      display: none;
    `)}
  `}
`

const StyledSvgIcon = styled(SvgIcon)`
  ${({
    theme: {
      colors: {
        brandColors: { brand_55 },
      },
    },
  }) => css`
    background-color: ${brand_55};
  `}
`

const StyledItemsContainerWrapper = styled.div`
  position: relative;
  width: 100%;
  overflow: hidden;
  padding: 0 15px;
  box-sizing: border-box;
`

const MoreAbout: FunctionComponent<MoreAboutProps> = (props) => {
  const itemsContainerRef = useRef<HTMLDivElement>(null)
  const isValidProps = isValidMoreAboutProps(props)
  const [prevArrowVisible, setPrevArrowVisible] = useState(false)
  const [nextArrowVisible, setNextArrowVisible] = useState(true)
  const viewportType = useViewportType()

  const [isScrolling, setIsScrolling] = useState(false)

  useEffect(() => {
    if (
      itemsContainerRef.current &&
      itemsContainerRef.current?.scrollWidth >
        itemsContainerRef.current?.clientWidth
    ) {
      setIsScrolling(true)
    } else {
      setIsScrolling(false)
    }
  }, [itemsContainerRef, viewportType])

  const onImpression = useCallback<TrackingFnType>(
    ({ pageMetadata: { title, id } }) => ({
      event: 'article_impression_more_about',
      eventCategory: 'article',
      eventLabel: `${id}:${stripHtml(title)}`,
      eventAction: 'impression_more_about',
    }),
    []
  )

  const onClick = useCallback<TrackingFnType<{ prevOrNext: 'prev' | 'next' }>>(
    ({ extraData: { prevOrNext } }) => ({
      event: 'arrowClick_mehrZumThema',
      eventCategory: 'related content',
      eventLabel: prevOrNext,
      eventAction: 'arrowClick_mehrZumThema',
    }),
    []
  )

  const trackedOnImpression = useTracking(onImpression)
  const trackedOnClick = useTracking(onClick)

  const viewportRef = useViewportTracking({
    track: isValidProps,
    onImpression: trackedOnImpression,
  })

  if (!isValidProps) {
    return null
  }

  const { items, title } = props

  return (
    <StyledMoreAbout ref={viewportRef} className="unblurred">
      <StyledTitle>{title}</StyledTitle>
      <StyledItemsContainerWrapper>
        <StyledItemsContainer ref={itemsContainerRef}>
          {items.map((item, index) =>
            item.image.crops ? (
              <MoreAboutItem
                key={item.link.targetArticleId ?? index}
                {...item}
              />
            ) : null
          )}
        </StyledItemsContainer>
        <StyledScrollingButton
          direction="prev"
          isVisible={prevArrowVisible}
          isScrolling={isScrolling}
          onClick={() => {
            scrollToItem({
              itemsContainerRef,
              prevOrNext: 'prev',
              setPrevArrowVisible,
              setNextArrowVisible,
            })()
            trackedOnClick({ prevOrNext: 'prev' })
          }}>
          <StyledSvgIcon iconName="gallery-previous" size={30} color="white" />
        </StyledScrollingButton>
        <StyledScrollingButton
          direction="next"
          isVisible={nextArrowVisible}
          isScrolling={isScrolling}
          onClick={() => {
            scrollToItem({
              itemsContainerRef,
              prevOrNext: 'next',
              setPrevArrowVisible,
              setNextArrowVisible,
            })()
            trackedOnClick({ prevOrNext: 'next' })
          }}>
          <StyledSvgIcon iconName="gallery-next" size={30} color="white" />
        </StyledScrollingButton>
      </StyledItemsContainerWrapper>
    </StyledMoreAbout>
  )
}

const widget: CookWidget = {
  kind: ['widget', 'more-about'],
  component: MoreAbout,
}

export default widget
